export type NoiseReductionType = 'near_field' | 'far_field';

const HEADSET_PATTERN = /headphone|headset|airpod|bluetooth|external/i;

export function getNoiseReductionType(stream: MediaStream): NoiseReductionType {
  const label = stream.getTracks()[0]?.label ?? '';
  return HEADSET_PATTERN.test(label) ? 'near_field' : 'far_field';
}

// Registers two listeners that keep noise_reduction current during a session:
//   - track 'ended': device physically disconnected → far_field
//   - mediaDevices 'devicechange': looks up the live track's deviceId in the enumerated
//     device list so we only check the mic that's actually in use, not any paired-but-idle
//     headset. A generation counter ensures rapid connect/disconnect can't let a stale
//     enumeration win and overwrite a more recent update.
// Returns a cleanup function — call it in stopSession.
export function registerAudioDeviceMonitor(
  track: MediaStreamTrack,
  getDataChannel: () => { readyState: string; send: (data: string) => void } | null,
  getCurrentType: () => NoiseReductionType,
  setCurrentType: (type: NoiseReductionType) => void,
  onUpdate: (type: NoiseReductionType) => void,
): () => void {
  const sendUpdate = (newType: NoiseReductionType) => {
    if (newType === getCurrentType()) return;
    // Always update the ref so session.created reads the correct type even if the
    // channel isn't open yet (e.g. track ends before the session is established).
    setCurrentType(newType);
    onUpdate(newType);
    const dc = getDataChannel();
    if (!dc || dc.readyState !== 'open') return;
    dc.send(JSON.stringify({
      type: 'session.update',
      session: { audio: { input: { noise_reduction: { type: newType } } } },
    }));
  };

  const onTrackEnded = () => sendUpdate('far_field');
  track.addEventListener('ended', onTrackEnded);

  let generation = 0;
  const trackDeviceId = track.getSettings().deviceId;

  const onDeviceChange = () => {
    const currentGeneration = ++generation;
    navigator.mediaDevices.enumerateDevices()
      .then(devices => {
        if (currentGeneration !== generation) return;
        const activeDevice = devices.find(
          d => d.kind === 'audioinput' && d.deviceId === trackDeviceId,
        );
        sendUpdate(
          activeDevice && HEADSET_PATTERN.test(activeDevice.label) ? 'near_field' : 'far_field',
        );
      })
      .catch(() => {});
  };
  navigator.mediaDevices.addEventListener('devicechange', onDeviceChange);

  return () => {
    track.removeEventListener('ended', onTrackEnded);
    navigator.mediaDevices.removeEventListener('devicechange', onDeviceChange);
  };
}
